import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Grid} from '@mui/material'
import {makeStyles} from '@oc/styles'
import {
    OcGenericDialogCheckboxEditor,
    OcGenericDialogSwitchEditor,
    OcGenericDialogStringEditor,
    OcGenericDialogNumberEditor,
    OcGenericDialogEnumEditor,
    OcGenericDialogRadiogroupEditor,
    OcGenericDialogDateTimeEditor
} from './OcGenericEditComponents'
import {OcGenericDialogFieldWidth, OcGenericDialogFieldBaseProps} from './OcGenericDialogInterfaces'
import {OcGenericDialogState} from './OcGenericEditState'
import {OcGenericDialogFieldType} from './OcGenericDialogInterfacesExport'
import {OcGenericDialogFieldResetWrapper} from './OcGenericDialogFieldResetWrapper'
import {OcDropzone} from '../OcFileUploadDialog/OcDropzoneComponent'

// copied from old MUI's Grid.tsx, use one from new MUI's Grid2.tsx when migrated to it
type GridSize = 'auto' | number

interface OcGenericDialogEditComponentProps<SourceType>
    extends Omit<OcGenericDialogFieldBaseProps<SourceType, any>, 'getValue'> {
    dialogState: OcGenericDialogState<SourceType>
    group: string
    name: string
    getValue: (group: string, field: string) => any
    stickyValues?: string[]
}

export function getOcGenericDialogFieldGridPaddingStyle() {
    return {
        padding: '6px 12px'
    } as const
}

const useStyles = makeStyles({
    fieldGrid: {
        ...getOcGenericDialogFieldGridPaddingStyle()
    },
    fieldGridNoSidePadding: {
        padding: '6px 0'
    },
    fieldGridNoPadding: {
        padding: 0
    }
})

const genericDialogGetLgGridSize = (width: OcGenericDialogFieldWidth): GridSize => {
    switch (width) {
        case 1:
            return 4
        case 2:
            return 6
        default:
            return 12
    }
}

const genericDialogGetMdGridSize = (width: OcGenericDialogFieldWidth): GridSize => {
    switch (width) {
        case 1:
            return 6
        default:
            return 12
    }
}

const genericDialogGetSmGridSize = () => {
    return 12 as GridSize
}

export const getOcGenericDialogGridBreakPointConfiguration = (width: OcGenericDialogFieldWidth) => {
    return {
        xl: (width * 2) as GridSize,
        lg: genericDialogGetLgGridSize(width),
        md: genericDialogGetMdGridSize(width),
        sm: genericDialogGetSmGridSize()
    }
}

function _OcGenericDialogEditComponentProps<SourceType>(props: OcGenericDialogEditComponentProps<SourceType>) {
    const {
        dialogState,
        name,
        group,
        getValue,
        customEditor,
        type: fieldType,
        allowDecimal,
        decimalPlaces,
        minValue,
        maxValue,
        changeStep,
        label,
        noOptionsMessage,
        mandatory,
        suggestions,
        multiSelect,
        helperText,
        defaultValue,
        placeholder,
        lineType,
        maxRows,
        minRows,
        minDate,
        maxDate,
        dateOnly,
        allowedTypes
    } = props

    const onChange = React.useCallback(
        (value: any) => {
            dialogState.onChange(group, name, value)
        },
        [group, name, dialogState]
    )

    const getValueCallback = React.useCallback(() => {
        return getValue(group, name)
    }, [getValue, group, name])

    const {changedValue, isChanged, errorMessage, customValidationResult} = dialogState.getFieldInfo(group, name)

    switch (fieldType) {
        case OcGenericDialogFieldType.String:
            return (
                <OcGenericDialogStringEditor
                    fieldName={name}
                    getValue={getValueCallback}
                    changedValue={changedValue}
                    onChange={onChange}
                    isChanged={isChanged}
                    disabled={props.disabled || false}
                    errorMessage={errorMessage}
                    label={label}
                    mandatory={mandatory}
                    helperText={helperText}
                    defaultValue={defaultValue}
                    createMode={dialogState.createMode}
                    placeholder={placeholder}
                    lineType={lineType}
                    maxRows={maxRows}
                    minRows={minRows}
                />
            )
        case OcGenericDialogFieldType.Number:
            return (
                <OcGenericDialogNumberEditor
                    fieldName={name}
                    getValue={getValueCallback}
                    changedValue={changedValue}
                    onChange={onChange}
                    isChanged={isChanged}
                    disabled={props.disabled || false}
                    allowDecimal={allowDecimal}
                    decimalPlaces={decimalPlaces}
                    minValue={minValue}
                    maxValue={maxValue}
                    changeStep={changeStep}
                    errorMessage={errorMessage}
                    label={label}
                    mandatory={mandatory}
                    helperText={helperText}
                    defaultValue={defaultValue}
                    createMode={dialogState.createMode}
                    placeholder={placeholder}
                />
            )
        case OcGenericDialogFieldType.Enum:
            return (
                <OcGenericDialogEnumEditor
                    fieldName={name}
                    getValue={getValueCallback}
                    changedValue={changedValue}
                    onChange={onChange}
                    errorMessage={errorMessage}
                    isChanged={isChanged}
                    disabled={props.disabled || false}
                    label={label}
                    noOptionsMessage={noOptionsMessage}
                    suggestions={suggestions || []}
                    multiSelect={multiSelect}
                    mandatory={mandatory}
                    helperText={helperText}
                    defaultValue={defaultValue}
                    createMode={dialogState.createMode}
                    placeholder={placeholder}
                    stickyValues={props.stickyValues || []}
                />
            )
        case OcGenericDialogFieldType.Checkbox:
            return (
                <OcGenericDialogCheckboxEditor
                    fieldName={name}
                    getValue={getValueCallback}
                    changedValue={changedValue}
                    onChange={onChange}
                    isChanged={isChanged}
                    disabled={props.disabled || false}
                    label={label}
                    defaultValue={defaultValue}
                    createMode={dialogState.createMode}
                />
            )
        case OcGenericDialogFieldType.Switch:
            return (
                <OcGenericDialogSwitchEditor
                    fieldName={name}
                    getValue={getValueCallback}
                    changedValue={changedValue}
                    onChange={onChange}
                    isChanged={isChanged}
                    disabled={props.disabled || false}
                    label={label}
                    defaultValue={defaultValue}
                    createMode={dialogState.createMode}
                />
            )
        case OcGenericDialogFieldType.Radiogroup:
            return (
                <OcGenericDialogRadiogroupEditor
                    fieldName={name}
                    getValue={getValueCallback}
                    changedValue={changedValue}
                    onChange={onChange}
                    isChanged={isChanged}
                    disabled={props.disabled || false}
                    suggestions={suggestions || []}
                    defaultValue={defaultValue}
                    createMode={dialogState.createMode}
                    errorMessage={errorMessage}
                />
            )
        case OcGenericDialogFieldType.DateTime:
            return (
                <OcGenericDialogDateTimeEditor
                    fieldName={name}
                    label={label}
                    getValue={getValueCallback}
                    changedValue={changedValue}
                    onChange={onChange}
                    isChanged={isChanged}
                    disabled={props.disabled || false}
                    defaultValue={defaultValue}
                    createMode={dialogState.createMode}
                    errorMessage={errorMessage}
                    minDate={minDate}
                    maxDate={maxDate}
                    dateOnly={dateOnly}
                    mandatory={mandatory}
                    helperText={helperText}
                    placeholder={placeholder}
                />
            )
        case OcGenericDialogFieldType.Dropzone:
            return (
                <OcDropzone
                    setFile={onChange}
                    maxSizeInBytes={maxValue}
                    file={changedValue}
                    allowedFileTypes={allowedTypes as string[]}
                    errorMessage={errorMessage}
                    dropZoneMessage={placeholder}
                    disabled={props.disabled || false}
                    helperText={props.helperText}
                />
            )
        case OcGenericDialogFieldType.Custom:
            if (customEditor) {
                return (
                    <customEditor.editor
                        {...customEditor.additionalProps}
                        fieldName={name}
                        getValue={getValueCallback}
                        changedValue={changedValue || undefined}
                        onChange={onChange}
                        isChanged={isChanged}
                        disabled={props.disabled || false}
                        errorMessage={errorMessage}
                        defaultValue={defaultValue}
                        createMode={dialogState.createMode}
                        mandatory={mandatory}
                        customValidationResult={customValidationResult}
                    />
                )
            }
            throw new Error('OC generic dialog custom field doesn\'t specify "customEditor"')
        default:
            throw new Error(`Unknown type of OC generic dialog field: "${fieldType}"`)
    }
}

const OcGenericDialogEditComponent = observer(_OcGenericDialogEditComponentProps)

function _OcGenericDialogFieldWrapper<SourceType>(props: OcGenericDialogEditComponentProps<SourceType>) {
    const {classes} = useStyles()
    const isChanged = props.dialogState.isFieldChanged(props.group, props.name)

    const getGridStyles = (type: string) => {
        if (type === 'radiogroup') {
            return classes.fieldGridNoSidePadding
        } else if (type === 'custom') {
            return classes.fieldGridNoPadding
        }

        return classes.fieldGrid
    }

    return (
        <Grid
            container
            item
            className={getGridStyles(props.type)}
            {...getOcGenericDialogGridBreakPointConfiguration(props.width || 6)}
            data-cy={`oc-generic-dialog-field-${props.name}`}
        >
            <OcGenericDialogFieldResetWrapper<SourceType>
                createMode={props.dialogState.createMode}
                isChanged={isChanged}
                resetDisabled={props.disableReset}
                resetFuncParams={{groupName: props.group, fieldName: props.name}}
            >
                <OcGenericDialogEditComponent {...props} />
            </OcGenericDialogFieldResetWrapper>
        </Grid>
    )
}

export const OcGenericDialogFieldWrapper = observer(_OcGenericDialogFieldWrapper)
